import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { BusinessCard } from "@/components/BusinessCard";
import { useFavorites } from "@/hooks/useFavorites";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const FILTERS = [
  { key: "has_deals", label: "Deals" },
  { key: "home_based", label: "Home-Based" },
  { key: "hidden_gem", label: "Hidden Gems" },
  { key: "trending", label: "Trending" },
];

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const { favs, toggleFav } = useFavorites();

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const load = () => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (category) p.category = category;
    Object.keys(flags).forEach((k) => { if (flags[k]) p[k] = true; });
    api.get("/businesses", { params: p }).then((r) => { setItems(r.data); setLoading(false); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, flags]);
  useEffect(() => { setCategory(params.get("category") || ""); setQ(params.get("q") || ""); /* eslint-disable-next-line */ }, [params]);

  const submit = (e) => { e.preventDefault(); setParams(q ? { q } : {}); load(); };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 min-h-[70vh]">
      <h1 className="font-display text-4xl font-semibold tracking-tight mb-6">Explore Calgary</h1>

      <form onSubmit={submit} className="flex items-center bg-white rounded-full shadow-md border border-slate-100 p-2 max-w-2xl mb-6">
        <Search className="w-5 h-5 text-slate-400 ml-3" />
        <input data-testid="explore-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, category, city or keyword…"
          className="flex-1 bg-transparent border-none focus:ring-0 px-3 outline-none" />
        <Button type="submit" data-testid="explore-search-btn" className="rounded-full bg-orange-500 hover:bg-orange-600">Search</Button>
      </form>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <span className="text-sm text-slate-400 flex items-center gap-1 mr-1"><SlidersHorizontal className="w-4 h-4" /></span>
        <button data-testid="filter-all" onClick={() => setCategory("")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!category ? "bg-slate-900 text-white" : "bg-[#F3F0EA] text-slate-700 hover:bg-slate-200"}`}>All</button>
        {cats.map((c) => (
          <button key={c.id} data-testid={`filter-cat-${c.slug}`} onClick={() => setCategory(c.name)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${category === c.name ? "bg-orange-500 text-white" : "bg-[#F3F0EA] text-slate-700 hover:bg-orange-100"}`}>{c.name}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`toggle-${f.key}`} onClick={() => setFlags({ ...flags, [f.key]: !flags[f.key] })}
            className={`px-4 py-1.5 rounded-full text-sm border-2 transition-colors ${flags[f.key] ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20" data-testid="no-results">
          <p className="font-display text-2xl text-slate-700">No results found</p>
          <p className="text-slate-500 mt-2">Try a different search or filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="explore-results">
          {items.map((b, i) => (
            <BusinessCard key={b.id} biz={b} index={i} favorited={favs.includes(b.id)} onToggleFav={toggleFav} />
          ))}
        </div>
      )}
    </div>
  );
}
